import { useEffect, useMemo, useState } from "react";
import { advisoryApi } from "../advisoryApi";
import { SaveBar, Seg } from "../ui";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface FieldDef {
  id: string;
  name: string;
  type: string;
  required: boolean;
  order?: number;
  options?: { optionId: string; label: string; order?: number }[];
}
interface FieldValue {
  id: string;
  value: unknown;
}
interface FieldsData {
  definitions: { fields: FieldDef[] };
  values: { fields: FieldValue[] };
}

const asString = (v: unknown) => (v === null || v === undefined ? "" : String(v));

/**
 * Flow custom fields (`kind: field`) for one page — QP's wizard-field-renderer:
 * dropdown → select, radio → segmented choice, checkbox, text / number input.
 * Only the fields this page lists in /status are shown.
 */
export function FieldsStep(props: StepProps) {
  const { sessionId, steps, readOnly, onSaved } = props;
  const load = useStepLoad<FieldsData>(sessionId, "field", {});
  const saver = useSaver(onSaved);
  const [values, setValues] = useState<Record<string, string>>({});
  const [baseline, setBaseline] = useState<Record<string, string>>({});

  const pageFieldIds = useMemo(() => new Set(steps.map((s) => s.fieldId).filter(Boolean) as string[]), [steps]);
  const defs = useMemo(() => {
    const all = load.data?.definitions.fields ?? [];
    const shown = pageFieldIds.size ? all.filter((f) => pageFieldIds.has(f.id)) : all;
    return [...shown].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [load.data, pageFieldIds]);

  useEffect(() => {
    if (!load.data) return;
    const v: Record<string, string> = {};
    for (const f of load.data.values.fields ?? []) v[f.id] = asString(f.value);
    setValues(v);
    setBaseline(v);
  }, [load.data]);

  if (load.loading && !load.data) return <p className="muted">Loading fields…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;

  const dirty = defs.some((f) => (values[f.id] ?? "") !== (baseline[f.id] ?? ""));

  async function save() {
    const changed = defs
      .filter((f) => (values[f.id] ?? "") !== (baseline[f.id] ?? "") && (values[f.id] ?? "") !== "")
      .map((f) => ({ flowFieldId: f.id, value: f.type === "number" ? Number(values[f.id]) : f.type === "checkbox" ? values[f.id] === "true" : values[f.id] }));
    if (changed.length === 0) return;
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "field", {}, { values: changed }));
    if (r) setBaseline(values);
  }

  const set = (id: string, v: string) => setValues((prev) => ({ ...prev, [id]: v }));

  return (
    <section className="adv-step-card">
      <div className="adv-fields">
        {defs.map((f) => {
          const opts = [...(f.options ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          const v = values[f.id] ?? "";
          return (
            <div className="field" key={f.id}>
              <span>
                {f.name}
                {f.required && " *"}
              </span>
              {f.type === "dropdown" ? (
                <select value={v} onChange={(e) => set(f.id, e.target.value)} disabled={readOnly}>
                  <option value="">Choose…</option>
                  {opts.map((o) => (
                    <option key={o.optionId} value={o.optionId}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : f.type === "radio" ? (
                <Seg
                  value={v}
                  options={opts.map((o) => ({ value: o.optionId, label: o.label }))}
                  onChange={(nv) => set(f.id, nv)}
                  disabled={readOnly}
                  label={f.name}
                />
              ) : f.type === "checkbox" ? (
                <label className="adv-check-inline">
                  <input
                    type="checkbox"
                    checked={v === "true"}
                    onChange={(e) => set(f.id, e.target.checked ? "true" : "false")}
                    disabled={readOnly}
                  />
                  Yes
                </label>
              ) : (
                <input
                  type={f.type === "number" ? "number" : "text"}
                  value={v}
                  onChange={(e) => set(f.id, e.target.value)}
                  disabled={readOnly}
                />
              )}
            </div>
          );
        })}
        {defs.length === 0 && <p className="muted">This page has no fields to fill in.</p>}
      </div>
      <SaveBar onSave={save} saving={saver.saving} dirty={dirty} error={saver.error} readOnly={readOnly} />
    </section>
  );
}
